// Modern route preloading utility
// Uses browser-native resource hints for better performance
import { supportsRequestIdleCallback } from './browserSupport';

/**
 * Preload a route chunk using dynamic import
 * This is more efficient than manual import() calls
 */
export function preloadRoute(routePath: string): void {
  // Map routes to their chunk imports
  const routeChunks: Record<string, () => Promise<any>> = {
    '/dashboard': () => import('@features/dashboard/Dashboard'),
    '/cloud-explorer': () => import('@features/cloud-explorer/CloudExplorer'),
    '/version': () => import('@features/version'),
    '/sharing': () => import('@features/sharing'),
    '/youtube': () => import('@features/youtube'),
    '/settings': () => import('@features/settings'),
    '/billing': () => import('@features/billing'),
    '/support': () => import('@features/support'),
  };

  const preloadFn = routeChunks[routePath];
  if (preloadFn) {
    // Use requestIdleCallback for non-blocking preload (with browser support check)
    if (supportsRequestIdleCallback()) {
      requestIdleCallback(() => {
        preloadFn().catch(() => {
          // Silently fail - chunk will load on navigation anyway
        });
        // NOTE: Files are NOT preloaded on hover - they will load when the page is actually opened
        // This prevents unnecessary API calls when just hovering over the navigation item
      }, { timeout: 2000 });
    } else {
      // Fallback for browsers without requestIdleCallback
      setTimeout(() => {
        preloadFn().catch(() => {});
        // NOTE: Files are NOT preloaded on hover - they will load when the page is actually opened
        // This prevents unnecessary API calls when just hovering over the navigation item
      }, 100);
    }
  }
}

/**
 * Prefetch route using browser-native prefetch
 * More efficient than dynamic import for known routes
 */
export function prefetchRoute(routePath: string): void {
  // This would require knowing the chunk names at build time
  // For now, we use dynamic import, but this could be enhanced
  preloadRoute(routePath);
}

